import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Pill from '../components/ui/Pill';
import { EmptyState, SkeletonRows, ErrorNotice, InfoNotice } from '../components/ui/States';
import { fmt, dateShort } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';
import {
  PARTICIPANT_STATUSES,
  PARTICIPANT_TONE,
  PAYMENT_STATUS_LABEL,
  label,
  personName,
  errorMessage,
  inputClass,
  selectClass,
  money,
  toCents,
} from '../lib/programs';
import {
  Committee,
  CycleDetail,
  CommitteeMember,
  Composition,
  CountRow,
  Meeting,
  AGE_BAND_LABEL,
  COMMITTEE_ROLE_LABEL,
  COMPENSATION_LABEL,
  toIso,
} from '../lib/committees';

type Tab = 'roster' | 'selection' | 'meetings' | 'composition' | 'polls';
const TABS: [Tab, string][] = [
  ['roster', 'Roster'],
  ['selection', 'Selection'],
  ['meetings', 'Meetings'],
  ['composition', 'Composition'],
  ['polls', 'Availability polls'],
];
const th = 'py-2 pr-4';
const td = 'py-[9px] pr-4';
const headRow = 'text-left text-small text-ink-2 border-b border-line-strong';
const SPREADSHEET = /\.(xlsx|xls|xlsm|numbers)$/i;

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-CA', { timeZone: 'America/Toronto', dateStyle: 'medium', timeStyle: 'short' })
    : '—';
const roleLabel = (r: string) => COMMITTEE_ROLE_LABEL[r] ?? label(r);

function Field({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-small text-ink-2 mb-1">{title}</label>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- composition

function CountTable({ title, rows }: { title: string; rows: CountRow[] }) {
  return (
    <div>
      <div className="text-small text-ink-2 mb-1">{title}</div>
      {!rows.length ? (
        <p className="text-small text-ink-2">None</p>
      ) : (
        <ul className="text-table">
          {rows.map((r) => (
            <li key={r.value} className="flex justify-between border-b border-line py-1">
              <span>{r.value}</span>
              <span>{fmt(r.count)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CompositionView({ composition }: { composition: Composition }) {
  const e = composition.equity;
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <CountTable title="Role" rows={composition.byRole.map((r) => ({ ...r, value: roleLabel(r.value) }))} />
        <CountTable title="Who they speak for" rows={composition.byPerspective} />
        <CountTable title="Region" rows={composition.byRegion} />
        <CountTable title="Language" rows={composition.byLanguage} />
      </div>
      <div className="mt-6 border-t border-line pt-4">
        <div className="text-small text-ink-2 mb-2">
          Equity (aggregates only) · {e.answeredBy} of {composition.total} answered
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <ul className="text-table col-span-2">
            {e.booleans.map((b) => (
              <li key={b.key} className="flex justify-between border-b border-line py-1">
                <span>{b.label}</span>
                <span>
                  {fmt(b.yes)} <span className="text-ink-2">of {b.answered} answered</span>
                </span>
              </li>
            ))}
          </ul>
          <div className="col-span-2">
            <CountTable title={`Gender identity · ${e.genderIdentity.total} answered`} rows={e.genderIdentity.items} />
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- members

interface MemberForm {
  role: string;
  status: string;
  perspective: string;
  region: string;
  language: string;
  ageBand: string;
}

function MemberFields({
  detail,
  form,
  set,
  withStatus,
}: {
  detail: CycleDetail;
  form: MemberForm;
  set: (f: MemberForm) => void;
  withStatus: boolean;
}) {
  const youthOk = Boolean(detail.program.settings.youthDataApproved);
  return (
    <div className="grid grid-cols-2 gap-4 mt-4">
      <Field title="Role">
        <select className={inputClass} value={form.role} onChange={(e) => set({ ...form, role: e.target.value })}>
          {detail.roles.map((r) => (
            <option key={r} value={r}>
              {roleLabel(r)}
            </option>
          ))}
        </select>
      </Field>
      {withStatus ? (
        <Field title="Status">
          <select className={inputClass} value={form.status} onChange={(e) => set({ ...form, status: e.target.value })}>
            {PARTICIPANT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {label(s)}
              </option>
            ))}
          </select>
        </Field>
      ) : (
        <div />
      )}
      <Field title="Speaks for">
        <select
          className={inputClass}
          value={form.perspective}
          onChange={(e) => set({ ...form, perspective: e.target.value })}
        >
          <option value="">—</option>
          {detail.perspectives.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </select>
      </Field>
      <Field title="Age band">
        <select className={inputClass} value={form.ageBand} onChange={(e) => set({ ...form, ageBand: e.target.value })}>
          <option value="">—</option>
          {detail.ageBands
            .filter((b) => b !== 'under_18' || youthOk || form.ageBand === 'under_18')
            .map((b) => (
              <option key={b} value={b}>
                {AGE_BAND_LABEL[b] ?? b}
              </option>
            ))}
        </select>
      </Field>
      <Field title="Region">
        <input className={inputClass} value={form.region} onChange={(e) => set({ ...form, region: e.target.value })} />
      </Field>
      <Field title="Language">
        <input
          className={inputClass}
          value={form.language}
          onChange={(e) => set({ ...form, language: e.target.value })}
        />
      </Field>
      {!youthOk ? (
        <p className="col-span-2 text-small text-ink-2">
          Under 18 is not offered: youth members are not held on the platform until York’s privacy office has answered
          (a super admin turns it on in the settings). Never record a date of birth.
        </p>
      ) : (
        <p className="col-span-2 text-small text-ink-2">
          Members under 18 are held as contacts only, seen by admins only and never emailed by the platform. Never
          record a date of birth.
        </p>
      )}
    </div>
  );
}

const emptyForm = (role = 'member'): MemberForm => ({
  role,
  status: 'invited',
  perspective: '',
  region: '',
  language: '',
  ageBand: '',
});
const formBody = (f: MemberForm) => ({
  role: f.role,
  perspective: f.perspective || null,
  region: f.region || null,
  language: f.language || null,
  ageBand: f.ageBand || null,
});

function AddMember({ detail, onClose, onSaved }: { detail: CycleDetail; onClose: () => void; onSaved: () => void }) {
  const [mode, setMode] = useState<'member' | 'contact'>(
    detail.program.settings.externalMembers ? 'contact' : 'member',
  );
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [form, setForm] = useState<MemberForm>(emptyForm());
  const save = useMutation(
    async () =>
      api.post(`/committees/cycles/${detail.cycle.id}/members`, {
        ...formBody(form),
        status: form.status,
        ...(mode === 'member'
          ? { email }
          : { newContact: { name, email: email || null, organization: organization || null } }),
      }),
    { onSuccess: onSaved },
  );
  return (
    <Modal
      title={`Add to ${detail.program.name} ${detail.cycle.label}`}
      onClose={onClose}
      width={640}
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={save.isLoading || (mode === 'member' ? !email.trim() : !name.trim())}
            onClick={() => save.mutate()}
          >
            {save.isLoading ? 'Adding…' : 'Add'}
          </Button>
        </div>
      }
    >
      <div className="flex gap-2 mb-4">
        <Button size="sm" variant={mode === 'member' ? 'dark' : 'outline'} onClick={() => setMode('member')}>
          Member or existing contact
        </Button>
        <Button size="sm" variant={mode === 'contact' ? 'dark' : 'outline'} onClick={() => setMode('contact')}>
          New external member
        </Button>
      </div>
      {mode === 'member' ? (
        <Field title="Email of a member or a contact">
          <input className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <Field title="Name">
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field title="Email (optional)">
            <input className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field title="Organization (optional)">
            <input className={inputClass} value={organization} onChange={(e) => setOrganization(e.target.value)} />
          </Field>
          <p className="text-small text-ink-2 self-end">
            Saved as a contact of kind “External committee member”; an existing contact with the email is reused.
          </p>
        </div>
      )}
      <MemberFields detail={detail} form={form} set={setForm} withStatus />
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not add the member.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function EditMember({
  detail,
  member,
  mutable,
  onClose,
  onSaved,
}: {
  detail: CycleDetail;
  member: CommitteeMember;
  mutable: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<MemberForm>({
    role: member.role,
    status: member.status,
    perspective: member.perspective ?? '',
    region: member.region ?? '',
    language: member.language ?? '',
    ageBand: member.ageBand ?? '',
  });
  const [note, setNote] = useState('');
  const save = useMutation(
    async () => {
      await api.patch(`/committees/members/${member.id}`, formBody(form));
      if (form.status !== member.status)
        await api.post(`/committees/members/${member.id}/status`, { status: form.status, note: note || undefined });
    },
    { onSuccess: onSaved },
  );
  return (
    <Modal
      title={personName(member.person)}
      onClose={onClose}
      width={640}
      footer={
        mutable && !member.restricted ? (
          <div className="flex justify-end gap-3 w-full">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
              {save.isLoading ? 'Saving…' : 'Save'}
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="text-table space-y-1">
        {member.person.email ? <div className="text-ink-2">{member.person.email}</div> : null}
        {member.person.type === 'member' && member.person.id ? (
          <Link className="underline text-small" to={`/analytics/members/${member.person.id}`}>
            Member 360
          </Link>
        ) : null}
        {member.eligibility?.flags.length ? (
          <ul className="text-small mt-2">
            {member.eligibility.flags.map((f, i) => (
              <li key={i} className={f.severity === 'fail' ? 'text-warning' : 'text-ink-2'}>
                {f.message}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {mutable && !member.restricted ? (
        <>
          <MemberFields detail={detail} form={form} set={setForm} withStatus />
          <div className="mt-4">
            <Field title="Note for the status change (optional)">
              <input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          </div>
        </>
      ) : null}
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice>
        </div>
      ) : null}
      <div className="text-small text-ink-2 mt-6 mb-2">History</div>
      <ul className="text-small space-y-1">
        {member.history.map((h, i) => (
          <li key={i} className="border-b border-line pb-1">
            {dateShort(h.at)}: {h.from ? `${label(h.from)} → ` : ''}
            {label(h.to)}
            {h.note ? <div className="text-ink-2">{h.note}</div> : null}
          </li>
        ))}
      </ul>
    </Modal>
  );
}

function MessageModal({
  detail,
  participantIds,
  onClose,
}: {
  detail: CycleDetail;
  participantIds: string[];
  onClose: () => void;
}) {
  const [kind, setKind] = useState('invitation');
  const [meetingId, setMeetingId] = useState(detail.meetings[detail.meetings.length - 1]?.id ?? '');
  const [responseBy, setResponseBy] = useState('');
  const [responseByFr, setResponseByFr] = useState('');
  const [joinInfo, setJoinInfo] = useState('');
  const body = {
    kind,
    participantIds,
    meetingId: kind === 'reminder' ? meetingId : undefined,
    vars: { responseBy, responseByFr, joinInfo },
  };
  const preview = useQuery(
    ['committee-message', JSON.stringify(body)],
    async () => (await api.post(`/committees/cycles/${detail.cycle.id}/messages/preview`, body)).data.data,
    { retry: false },
  );
  const send = useMutation(
    async () => (await api.post(`/committees/cycles/${detail.cycle.id}/messages/send`, body)).data.data,
  );
  const data = preview.data as
    | {
        recipients: { participantId: string; name: string; email: string | null; ok: boolean; reason: string | null }[];
        missing: string[];
        outbox: boolean;
        rendered: { subject: string; text: string } | null;
      }
    | undefined;
  return (
    <Modal
      title="Prepare a message"
      onClose={onClose}
      width={720}
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            disabled={!data?.rendered || send.isLoading || Boolean(send.data)}
            onClick={() => send.mutate()}
          >
            {send.isLoading ? 'Sending…' : data?.outbox ? 'Put in the outbox' : 'Send'}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <Field title="Message">
          <select className={inputClass} value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="invitation">Invitation</option>
            <option value="confirmation">Confirmation of selection</option>
            <option value="reminder">Meeting reminder</option>
          </select>
        </Field>
        {kind === 'invitation' ? (
          <>
            <Field title="Reply by (English)">
              <input
                className={inputClass}
                placeholder="May 15, 2027"
                value={responseBy}
                onChange={(e) => setResponseBy(e.target.value)}
              />
            </Field>
            <Field title="Reply by (French)">
              <input
                className={inputClass}
                placeholder="15 mai 2027"
                value={responseByFr}
                onChange={(e) => setResponseByFr(e.target.value)}
              />
            </Field>
          </>
        ) : null}
        {kind === 'reminder' ? (
          <>
            <Field title="Meeting">
              <select className={inputClass} value={meetingId} onChange={(e) => setMeetingId(e.target.value)}>
                {detail.meetings.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title} · {when(m.startsAt)}
                  </option>
                ))}
              </select>
            </Field>
            <Field title="How to join (link or place)">
              <input className={inputClass} value={joinInfo} onChange={(e) => setJoinInfo(e.target.value)} />
            </Field>
          </>
        ) : null}
      </div>
      {preview.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(preview.error, 'Could not prepare the message.')}</ErrorNotice>
        </div>
      ) : null}
      {data ? (
        <div className="mt-4">
          <div className="text-small text-ink-2 mb-1">Recipients</div>
          <ul className="text-small">
            {data.recipients.map((r) => (
              <li key={r.participantId} className={r.ok ? '' : 'text-ink-2'}>
                {r.name}
                {r.email ? ` <${r.email}>` : ''}
                {r.reason ? ` — not sent: ${r.reason}` : ''}
              </li>
            ))}
          </ul>
          {data.missing.length ? (
            <div className="mt-3">
              <InfoNotice>Missing: {data.missing.join(', ')}</InfoNotice>
            </div>
          ) : null}
          {data.rendered ? (
            <div className="mt-4 border border-line p-3">
              <div className="text-small font-semibold">{data.rendered.subject}</div>
              <pre className="text-small whitespace-pre-wrap mt-2 font-sans">{data.rendered.text}</pre>
            </div>
          ) : null}
        </div>
      ) : null}
      {send.data ? (
        <div className="mt-3">
          <InfoNotice>
            Done:{' '}
            {Object.entries((send.data as { statuses: Record<string, number> }).statuses)
              .map(([s, n]) => `${n} ${s.replace('_', ' ')}`)
              .join(', ') || 'nothing sent'}
            .
          </InfoNotice>
        </div>
      ) : null}
      {send.error ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(send.error, 'Could not send.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function Roster({
  detail,
  mutable,
  isAdmin,
  refresh,
}: {
  detail: CycleDetail;
  mutable: boolean;
  isAdmin: boolean;
  refresh: () => void;
}) {
  const [status, setStatus] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<CommitteeMember | null>(null);
  const [messaging, setMessaging] = useState(false);
  const sync = useMutation(async () => api.post('/committees/management/sync', { year: detail.cycle.year }), {
    onSuccess: refresh,
  });
  const rows = detail.roster.filter((r) => !status || r.status === status);
  const perspective = (k: string | null) => detail.perspectives.find((p) => p.key === k)?.label ?? (k ? label(k) : '—');
  return (
    <Panel>
      <PanelHeader title="Roster" note={fmt(detail.roster.length)}>
        <select className={selectClass} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Every status</option>
          {PARTICIPANT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {label(s)}
            </option>
          ))}
        </select>
        {mutable && detail.program.key === 'committee_management' ? (
          <Button size="sm" disabled={sync.isLoading} onClick={() => sync.mutate()}>
            {sync.isLoading ? 'Syncing…' : 'Sync from PI and Staff groups'}
          </Button>
        ) : null}
        {mutable ? (
          <Button size="sm" disabled={!chosen.length} onClick={() => setMessaging(true)}>
            Prepare message ({chosen.length})
          </Button>
        ) : null}
        {mutable ? (
          <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
            Add member
          </Button>
        ) : null}
      </PanelHeader>
      <PanelBody>
        {sync.error ? (
          <div className="mb-3">
            <ErrorNotice>{errorMessage(sync.error, 'Could not sync.')}</ErrorNotice>
          </div>
        ) : null}
        {!rows.length ? (
          <EmptyState title="Nobody here yet">
            Add members, or let recruitment applications arrive through the Committee applications form family.
          </EmptyState>
        ) : (
          <table className="w-full">
            <thead>
              <tr className={headRow}>
                {mutable ? <th className={th} /> : null}
                <th className={th}>Name</th>
                <th className={th}>Role</th>
                <th className={th}>Speaks for</th>
                <th className={th}>Region · language</th>
                <th className={th}>Status</th>
                <th className={th}>Eligibility</th>
                <th className={th}>Meetings</th>
                {isAdmin ? <th className={th}>Compensation</th> : null}
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id} className="border-b border-line text-table align-top">
                  {mutable ? (
                    <td className={td}>
                      <input
                        type="checkbox"
                        checked={chosen.includes(m.id)}
                        onChange={() =>
                          setChosen(chosen.includes(m.id) ? chosen.filter((x) => x !== m.id) : [...chosen, m.id])
                        }
                      />
                    </td>
                  ) : null}
                  <td className={td}>
                    <div>{personName(m.person)}</div>
                    <div className="text-small text-ink-2">
                      {m.restricted ? 'Under 18' : m.person.type === 'contact' ? 'External' : m.person.email}
                      {!m.restricted && m.ageBand ? ` · ${AGE_BAND_LABEL[m.ageBand] ?? m.ageBand}` : ''}
                    </div>
                  </td>
                  <td className={td}>{roleLabel(m.role)}</td>
                  <td className={td}>{perspective(m.perspective)}</td>
                  <td className={`${td} text-ink-2`}>{[m.region, m.language].filter(Boolean).join(' · ') || '—'}</td>
                  <td className={td}>
                    <Pill tone={PARTICIPANT_TONE[m.status] ?? 'planned'}>{label(m.status)}</Pill>
                  </td>
                  <td className={td}>
                    {m.eligibility ? (
                      m.eligibility.eligible === true ? (
                        <Pill tone="healthy">Eligible</Pill>
                      ) : m.eligibility.eligible === false ? (
                        <Pill tone="failed">Check</Pill>
                      ) : (
                        <Pill tone="planned">Unanswered</Pill>
                      )
                    ) : (
                      <span className="text-ink-2">—</span>
                    )}
                  </td>
                  <td className={td}>
                    {m.meetingsAttended ?? 0} / {detail.meetingsHeld}
                  </td>
                  {isAdmin ? (
                    <td className={`${td} text-small text-ink-2`}>
                      {Object.entries(m.payments ?? {})
                        .map(([s, n]) => `${n} ${PAYMENT_STATUS_LABEL[s] ?? s}`)
                        .join(', ') || '—'}
                    </td>
                  ) : null}
                  <td className={`${td} text-right`}>
                    <Button variant="ghost" size="sm" onClick={() => setOpen(m)}>
                      {mutable && !m.restricted ? 'Edit' : 'History'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PanelBody>
      {adding ? (
        <AddMember
          detail={detail}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            refresh();
          }}
        />
      ) : null}
      {open ? (
        <EditMember
          detail={detail}
          member={open}
          mutable={mutable}
          onClose={() => setOpen(null)}
          onSaved={() => {
            setOpen(null);
            refresh();
          }}
        />
      ) : null}
      {messaging ? <MessageModal detail={detail} participantIds={chosen} onClose={() => setMessaging(false)} /> : null}
    </Panel>
  );
}

// ---------------------------------------------------------------- selection

function Selection({ detail, mutable, refresh }: { detail: CycleDetail; mutable: boolean; refresh: () => void }) {
  const applicants = detail.roster.filter((r) => ['invited', 'applied'].includes(r.status));
  const [chosen, setChosen] = useState<string[]>([]);
  const preview = useQuery<{ seated: number; adding: number; composition: Composition }>(
    ['committee-selection', detail.cycle.id, chosen.join(',')],
    async () =>
      (
        await api.get(`/committees/cycles/${detail.cycle.id}/selection`, {
          params: { participantIds: chosen.join(',') || undefined },
        })
      ).data.data,
    { keepPreviousData: true },
  );
  const confirm = useMutation(
    async () =>
      (await api.post(`/committees/cycles/${detail.cycle.id}/selection`, { participantIds: chosen })).data.data,
    {
      onSuccess: () => {
        setChosen([]);
        refresh();
      },
    },
  );
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-5">
      <Panel>
        <PanelHeader title="Applicants" note={fmt(applicants.length)}>
          {mutable ? (
            <Button
              size="sm"
              variant="primary"
              disabled={!chosen.length || confirm.isLoading}
              onClick={() => confirm.mutate()}
            >
              Confirm {chosen.length} selected
            </Button>
          ) : null}
        </PanelHeader>
        <PanelBody>
          {!applicants.length ? (
            <EmptyState title="No applicants">Invited and applied people appear here.</EmptyState>
          ) : (
            <ul className="text-table">
              {applicants.map((a) => (
                <li key={a.id} className="flex items-start gap-3 py-2 border-b border-line">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={chosen.includes(a.id)}
                    onChange={() =>
                      setChosen(chosen.includes(a.id) ? chosen.filter((x) => x !== a.id) : [...chosen, a.id])
                    }
                  />
                  <div className="flex-1">
                    <div>{personName(a.person)}</div>
                    <div className="text-small text-ink-2">
                      {[a.perspective && label(a.perspective), a.region, a.language].filter(Boolean).join(' · ')}
                    </div>
                    {a.eligibility?.flags.map((f, i) => (
                      <div key={i} className={`text-small ${f.severity === 'fail' ? 'text-warning' : 'text-ink-2'}`}>
                        {f.message}
                      </div>
                    ))}
                  </div>
                  <Pill tone={PARTICIPANT_TONE[a.status] ?? 'planned'}>{label(a.status)}</Pill>
                </li>
              ))}
            </ul>
          )}
          {confirm.error ? (
            <div className="mt-3">
              <ErrorNotice>{errorMessage(confirm.error, 'Could not record the selection.')}</ErrorNotice>
            </div>
          ) : null}
        </PanelBody>
      </Panel>
      <Panel>
        <PanelHeader
          title="Composition if confirmed"
          note={preview.data ? `${preview.data.seated} seated + ${preview.data.adding}` : undefined}
        />
        <PanelBody>
          {preview.isLoading ? (
            <SkeletonRows rows={4} />
          ) : preview.data ? (
            <CompositionView composition={preview.data.composition} />
          ) : null}
        </PanelBody>
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------- meetings

function MeetingForm({
  detail,
  meeting,
  onClose,
  onSaved,
}: {
  detail: CycleDetail;
  meeting: Meeting | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const local = (iso: string | null) =>
    iso ? new Date(new Date(iso).getTime() - new Date(iso).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '';
  const [f, setF] = useState({
    title: meeting?.title ?? `${detail.program.name} meeting`,
    startsAt: local(meeting?.startsAt ?? null),
    endsAt: local(meeting?.endsAt ?? null),
    mode: meeting?.mode ?? 'online',
    location: meeting?.location ?? '',
    briefUrl: meeting?.briefUrl ?? '',
    minutesUrl: meeting?.minutesUrl ?? '',
    minutesLabel: meeting?.minutesLabel ?? '',
  });
  const save = useMutation(
    async () => {
      const body = { ...f, startsAt: toIso(f.startsAt), endsAt: toIso(f.endsAt) };
      return meeting
        ? api.patch(`/committees/meetings/${meeting.id}`, body)
        : api.post(`/committees/cycles/${detail.cycle.id}/meetings`, body);
    },
    { onSuccess: onSaved },
  );
  return (
    <Modal
      title={meeting ? 'Edit meeting' : 'Add meeting'}
      onClose={onClose}
      width={640}
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!f.title.trim() || !f.startsAt || save.isLoading}
            onClick={() => save.mutate()}
          >
            {save.isLoading ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Field title="Title">
            <input className={inputClass} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          </Field>
        </div>
        <Field title="Starts">
          <input
            type="datetime-local"
            className={inputClass}
            value={f.startsAt}
            onChange={(e) => setF({ ...f, startsAt: e.target.value })}
          />
        </Field>
        <Field title="Ends (optional)">
          <input
            type="datetime-local"
            className={inputClass}
            value={f.endsAt}
            onChange={(e) => setF({ ...f, endsAt: e.target.value })}
          />
        </Field>
        <Field title="Mode">
          <select className={inputClass} value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}>
            <option value="online">Online</option>
            <option value="in_person">In person</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </Field>
        <Field title="Location (optional)">
          <input className={inputClass} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} />
        </Field>
        <div className="col-span-2">
          <Field title="Brief (link, optional)">
            <input
              className={inputClass}
              placeholder="https://"
              value={f.briefUrl}
              onChange={(e) => setF({ ...f, briefUrl: e.target.value })}
            />
          </Field>
        </div>
        <Field title="Minutes (link, optional)">
          <input
            className={inputClass}
            placeholder="https://"
            value={f.minutesUrl}
            onChange={(e) => setF({ ...f, minutesUrl: e.target.value })}
          />
        </Field>
        <Field title="Minutes label (optional)">
          <input
            className={inputClass}
            value={f.minutesLabel}
            onChange={(e) => setF({ ...f, minutesLabel: e.target.value })}
          />
        </Field>
        <p className="col-span-2 text-small text-ink-2">
          Minutes and notes are not stored here: they go to the knowledge base as restricted documents. Keep only the
          link.
        </p>
      </div>
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save the meeting.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

interface ZoomResult {
  dryRun: boolean;
  parsed: number;
  matched: number;
  unmatched: number;
  warnings: string[];
}

function ZoomUpload({ meeting, onClose, onDone }: { meeting: Meeting; onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<{ name: string; content: string } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const path = `/committees/meetings/${meeting.id}/zoom`;
  const preview = useMutation(
    async (f: { name: string; content: string }) =>
      (await api.post(path, { fileName: f.name, content: f.content }, { timeout: 120000 })).data.data as ZoomResult,
  );
  const confirm = useMutation(
    async () =>
      (await api.post(path, { fileName: file?.name, content: file?.content, confirm: true }, { timeout: 120000 })).data
        .data as ZoomResult,
    { onSuccess: onDone },
  );
  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFile(null);
    setFileError(null);
    preview.reset();
    confirm.reset();
    if (!f) return;
    if (SPREADSHEET.test(f.name)) {
      setFileError(
        'This is an Excel file. Open it in Excel, choose File → Save As → CSV UTF-8 (.csv), then upload the .csv.',
      );
      return;
    }
    const content = await f.text();
    setFile({ name: f.name, content });
    preview.mutate({ name: f.name, content });
  };
  const result = confirm.data ?? preview.data;
  return (
    <Modal
      title="Upload Zoom attendee report"
      onClose={onClose}
      footer={
        confirm.data ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button disabled={!preview.data || confirm.isLoading} onClick={() => confirm.mutate()}>
              {confirm.isLoading ? 'Recording…' : 'Record attendance'}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </>
        )
      }
    >
      <p className="text-small">
        In Zoom: Reports → Usage → Participants, export as CSV. Attendees are matched to the roster by email; nobody
        else is stored.
      </p>
      {!confirm.data ? <input type="file" accept=".csv,text/csv" onChange={pick} className="mt-3 text-small" /> : null}
      {fileError ? (
        <div className="mt-3">
          <ErrorNotice>{fileError}</ErrorNotice>
        </div>
      ) : null}
      {preview.error ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(preview.error, 'Could not read the report.')}</ErrorNotice>
        </div>
      ) : null}
      {confirm.error ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(confirm.error, 'Could not record attendance.')}</ErrorNotice>
        </div>
      ) : null}
      {result ? (
        <div className="mt-4 border-t border-line pt-4 text-table">
          <div className="text-small text-ink-2 mb-2">{confirm.data ? 'Recorded' : 'Preview, nothing saved yet'}</div>
          <div>
            {result.parsed} in the report · {result.matched} on the roster · {result.unmatched} not on the roster (not
            stored)
          </div>
          {result.warnings.map((w, i) => (
            <div key={i} className="text-ink-2 text-small">
              {w}
            </div>
          ))}
        </div>
      ) : null}
    </Modal>
  );
}

function MeetingDetail({
  meetingId,
  mutable,
  onChanged,
}: {
  meetingId: string;
  mutable: boolean;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const q = useQuery<{
    meeting: Meeting;
    compensation: { kind: string; amountCents: number | null } | null;
    roster: CommitteeMember[];
  }>(['committee-meeting', meetingId], async () => (await api.get(`/committees/meetings/${meetingId}`)).data.data);
  const [ticks, setTicks] = useState<Record<string, boolean>>({});
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (q.data) setTicks(Object.fromEntries(q.data.roster.map((r) => [r.id, Boolean(r.attendance?.attended)])));
  }, [q.data]);
  const changed = q.data ? q.data.roster.filter((r) => Boolean(r.attendance?.attended) !== Boolean(ticks[r.id])) : [];
  const save = useMutation(
    async () =>
      (
        await api.post(`/committees/meetings/${meetingId}/attendance`, {
          entries: changed.map((r) => ({ participantId: r.id, attended: Boolean(ticks[r.id]) })),
        })
      ).data.data as { warnings: { participantId: string; message: string }[] },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['committee-meeting', meetingId]);
        onChanged();
      },
    },
  );
  const reload = () => {
    queryClient.invalidateQueries(['committee-meeting', meetingId]);
    onChanged();
  };
  if (q.isLoading) return <SkeletonRows rows={4} />;
  if (q.error || !q.data) return <ErrorNotice>Could not load the meeting.</ErrorNotice>;
  const { meeting, compensation, roster } = q.data;
  const seated = roster.filter((r) => ['selected', 'active', 'completed'].includes(r.status) || r.attendance);
  return (
    <>
      <div className="text-table mb-3">
        <div className="font-semibold">{meeting.title}</div>
        <div className="text-ink-2">
          {when(meeting.startsAt)} · {label(meeting.mode ?? 'online')}
          {meeting.location ? ` · ${meeting.location}` : ''}
        </div>
        <div className="flex gap-4 mt-1 text-small">
          {meeting.briefUrl ? (
            <a className="underline" href={meeting.briefUrl} target="_blank" rel="noreferrer">
              Brief
            </a>
          ) : null}
          {meeting.minutesUrl ? (
            <a className="underline" href={meeting.minutesUrl} target="_blank" rel="noreferrer">
              {meeting.minutesLabel || 'Minutes'}
            </a>
          ) : null}
        </div>
        <div className="text-small text-ink-2 mt-1">
          {compensation
            ? `Each attending member is owed ${COMPENSATION_LABEL[compensation.kind].toLowerCase()} ${money(compensation.amountCents)} (ledger, status only).`
            : 'No compensation for this committee.'}
        </div>
      </div>
      <table className="w-full">
        <thead>
          <tr className={headRow}>
            <th className={th}>Attended</th>
            <th className={th}>Name</th>
            <th className={th}>Role</th>
            <th className={th}>How</th>
          </tr>
        </thead>
        <tbody>
          {seated.map((r) => (
            <tr key={r.id} className="border-b border-line text-table">
              <td className={td}>
                <input
                  type="checkbox"
                  disabled={!mutable}
                  checked={Boolean(ticks[r.id])}
                  onChange={() => setTicks({ ...ticks, [r.id]: !ticks[r.id] })}
                />
              </td>
              <td className={td}>{personName(r.person)}</td>
              <td className={td}>{roleLabel(r.role)}</td>
              <td className={`${td} text-ink-2`}>
                {r.attendance?.attended
                  ? `${r.attendance.source === 'zoom' ? 'Zoom' : 'Ticked'}${r.attendance.minutes ? `, ${r.attendance.minutes} min` : ''}`
                  : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {mutable ? (
        <div className="flex gap-3 mt-4">
          <Button
            variant="primary"
            size="sm"
            disabled={!changed.length || save.isLoading}
            onClick={() => save.mutate()}
          >
            {save.isLoading ? 'Saving…' : `Save attendance (${changed.length})`}
          </Button>
          <Button size="sm" onClick={() => setUploading(true)}>
            Upload Zoom report
          </Button>
        </div>
      ) : null}
      {save.data?.warnings.length ? (
        <div className="mt-3">
          <InfoNotice>{save.data.warnings.map((w) => w.message).join('; ')}</InfoNotice>
        </div>
      ) : null}
      {save.error ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(save.error, 'Could not save attendance.')}</ErrorNotice>
        </div>
      ) : null}
      {uploading ? <ZoomUpload meeting={meeting} onClose={() => setUploading(false)} onDone={reload} /> : null}
    </>
  );
}

function Meetings({ detail, mutable, refresh }: { detail: CycleDetail; mutable: boolean; refresh: () => void }) {
  const [selected, setSelected] = useState<string | null>(detail.meetings[detail.meetings.length - 1]?.id ?? null);
  const [editing, setEditing] = useState<Meeting | 'new' | null>(null);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-5">
      <Panel>
        <PanelHeader title="Meetings" note={fmt(detail.meetings.length)}>
          {mutable ? (
            <Button size="sm" variant="primary" onClick={() => setEditing('new')}>
              Add meeting
            </Button>
          ) : null}
        </PanelHeader>
        <PanelBody>
          {!detail.meetings.length ? (
            <EmptyState title="No meetings yet">Add one, or close an availability poll on a chosen time.</EmptyState>
          ) : (
            <ul className="text-table">
              {detail.meetings.map((m) => (
                <li
                  key={m.id}
                  className={`flex items-center justify-between gap-3 py-2 border-b border-line ${m.id === selected ? 'font-semibold' : ''}`}
                >
                  <button className="text-left flex-1" onClick={() => setSelected(m.id)}>
                    <div>{m.title}</div>
                    <div className="text-small text-ink-2 font-normal">
                      {when(m.startsAt)} · {m.attended ?? 0} attended
                    </div>
                  </button>
                  {mutable ? (
                    <Button variant="ghost" size="sm" onClick={() => setEditing(m)}>
                      Edit
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </PanelBody>
      </Panel>
      <Panel>
        <PanelHeader title="Attendance" />
        <PanelBody>
          {selected ? (
            <MeetingDetail key={selected} meetingId={selected} mutable={mutable} onChanged={refresh} />
          ) : (
            <p className="text-small text-ink-2">Choose a meeting.</p>
          )}
        </PanelBody>
      </Panel>
      {editing ? (
        <MeetingForm
          detail={detail}
          meeting={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- composition report

function CompositionReport({ detail }: { detail: CycleDetail }) {
  const q = useQuery<{
    composition: Composition;
    attendance: { meetingsHeld: number; attendances: number; possible: number; rate: number | null };
    compensation: { kind: string; byStatus: Record<string, number> };
  }>(
    ['committee-composition', detail.cycle.id],
    async () => (await api.get(`/committees/cycles/${detail.cycle.id}/composition`)).data.data,
  );
  if (q.isLoading) return <SkeletonRows rows={6} />;
  if (q.error || !q.data) return <ErrorNotice>Could not build the report.</ErrorNotice>;
  const { composition, attendance, compensation } = q.data;
  return (
    <Panel>
      <PanelHeader title={`${detail.program.name} ${detail.cycle.label}`} note={`${composition.total} members`} />
      <PanelBody>
        <div className="grid grid-cols-3 gap-5 mb-6 text-table">
          <div>
            <div className="text-small text-ink-2">Attendance rate</div>
            <div className="font-semibold text-[26px]">{attendance.rate === null ? '—' : `${attendance.rate}%`}</div>
            <div className="text-ink-2 text-small">
              {attendance.attendances} of {attendance.possible} seats across {attendance.meetingsHeld} meeting(s)
            </div>
          </div>
          <div>
            <div className="text-small text-ink-2">Compensation</div>
            <div className="font-semibold text-[20px]">
              {COMPENSATION_LABEL[compensation.kind] ?? compensation.kind}
            </div>
            <div className="text-ink-2 text-small">
              {Object.entries(compensation.byStatus)
                .map(([s, n]) => `${n} ${PAYMENT_STATUS_LABEL[s] ?? s}`)
                .join(', ') || 'No entries'}
            </div>
          </div>
        </div>
        <CompositionView composition={composition} />
      </PanelBody>
    </Panel>
  );
}

// ---------------------------------------------------------------- polls

interface Poll {
  id: string;
  title: string;
  state: string;
  chosenSlotId: string | null;
  slots: { id: string; startsAt: string; endsAt: string | null; yes: number }[];
  invited: number;
  answered: number;
  people: { participantId: string; name: string | null; respondedAt: string | null; choices: string[] }[];
}

function Polls({ detail, mutable, refresh }: { detail: CycleDetail; mutable: boolean; refresh: () => void }) {
  const queryClient = useQueryClient();
  const polls = useQuery<Poll[]>(
    ['committee-polls', detail.cycle.id],
    async () => (await api.get(`/committees/cycles/${detail.cycle.id}/polls`)).data.data,
  );
  const [title, setTitle] = useState('');
  const [slots, setSlots] = useState<string[]>(['', '']);
  const [links, setLinks] = useState<{ link: string; name: string }[] | null>(null);
  const reload = () => queryClient.invalidateQueries(['committee-polls', detail.cycle.id]);
  const create = useMutation(
    async () =>
      api.post(`/committees/cycles/${detail.cycle.id}/polls`, {
        title: title || undefined,
        slots: slots.filter(Boolean).map((s) => ({ startsAt: toIso(s) })),
      }),
    {
      onSuccess: () => {
        setTitle('');
        setSlots(['', '']);
        reload();
      },
    },
  );
  const seated = detail.roster.filter((r) => ['selected', 'active'].includes(r.status) && !r.restricted);
  const issue = useMutation(
    async (pollId: string) =>
      (await api.post(`/committees/polls/${pollId}/links`, { participantIds: seated.map((s) => s.id) })).data.data as {
        links: { participantId: string; link: string }[];
      },
    {
      onSuccess: (d) => {
        setLinks(
          d.links.map((l) => ({
            link: l.link,
            name: personName(seated.find((s) => s.id === l.participantId)!.person),
          })),
        );
        reload();
      },
    },
  );
  const close = useMutation(
    async ({ pollId, slotId }: { pollId: string; slotId: string }) =>
      api.post(`/committees/polls/${pollId}/close`, { chosenSlotId: slotId, createMeeting: true }),
    {
      onSuccess: () => {
        reload();
        refresh();
      },
    },
  );
  return (
    <div className="space-y-5">
      {mutable ? (
        <Panel>
          <PanelHeader title="New poll" />
          <PanelBody>
            <div className="grid grid-cols-2 gap-4">
              <Field title="Title (optional)">
                <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} />
              </Field>
              <div />
              {slots.map((s, i) => (
                <Field key={i} title={`Candidate time ${i + 1}`}>
                  <input
                    type="datetime-local"
                    className={inputClass}
                    value={s}
                    onChange={(e) => setSlots(slots.map((x, j) => (j === i ? e.target.value : x)))}
                  />
                </Field>
              ))}
            </div>
            <div className="flex gap-3 mt-4">
              <Button size="sm" onClick={() => setSlots([...slots, ''])}>
                Another time
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={!slots.some(Boolean) || create.isLoading}
                onClick={() => create.mutate()}
              >
                Create poll
              </Button>
            </div>
            {create.error ? (
              <div className="mt-3">
                <ErrorNotice>{errorMessage(create.error, 'Could not create the poll.')}</ErrorNotice>
              </div>
            ) : null}
          </PanelBody>
        </Panel>
      ) : null}
      {links ? (
        <Panel>
          <PanelHeader title="Links to send (shown once)">
            <Button size="sm" variant="ghost" onClick={() => setLinks(null)}>
              Hide
            </Button>
          </PanelHeader>
          <PanelBody>
            <p className="text-small text-ink-2 mb-2">
              Paste each link into your own email to that person. Each works once; the platform keeps only a
              fingerprint. Members under 18 get no link.
            </p>
            <ul className="text-small">
              {links.map((l) => (
                <li key={l.link}>
                  {l.name}: <span className="font-mono break-all">{l.link}</span>
                </li>
              ))}
            </ul>
          </PanelBody>
        </Panel>
      ) : null}
      {polls.isLoading ? (
        <SkeletonRows rows={3} />
      ) : !polls.data?.length ? (
        <EmptyState title="No polls">Create one to find a meeting time.</EmptyState>
      ) : (
        polls.data.map((p) => (
          <Panel key={p.id}>
            <PanelHeader title={p.title} note={`${p.answered} of ${p.invited} answered`}>
              <Pill tone={p.state === 'open' ? 'running' : 'inactive'}>{label(p.state)}</Pill>
              {mutable && p.state === 'open' ? (
                <Button size="sm" disabled={!seated.length || issue.isLoading} onClick={() => issue.mutate(p.id)}>
                  Links for selected and active ({seated.length})
                </Button>
              ) : null}
            </PanelHeader>
            <PanelBody>
              <table className="w-full">
                <thead>
                  <tr className={headRow}>
                    <th className={th}>Time</th>
                    <th className={th}>Works for</th>
                    <th className={th} />
                  </tr>
                </thead>
                <tbody>
                  {p.slots.map((s) => (
                    <tr
                      key={s.id}
                      className={`border-b border-line text-table ${p.chosenSlotId === s.id ? 'font-semibold' : ''}`}
                    >
                      <td className={td}>{when(s.startsAt)}</td>
                      <td className={td}>{s.yes}</td>
                      <td className={`${td} text-right`}>
                        {mutable && p.state === 'open' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={close.isLoading}
                            onClick={() => close.mutate({ pollId: p.id, slotId: s.id })}
                          >
                            Choose and add meeting
                          </Button>
                        ) : p.chosenSlotId === s.id ? (
                          'Chosen'
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </PanelBody>
          </Panel>
        ))
      )}
      {issue.error ? <ErrorNotice>{errorMessage(issue.error, 'Could not issue links.')}</ErrorNotice> : null}
    </div>
  );
}

// ---------------------------------------------------------------- settings

function Settings({
  detail,
  role,
  onClose,
  onSaved,
}: {
  detail: CycleDetail;
  role: string | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const s = detail.program.settings;
  const [kind, setKind] = useState(s.compensation?.kind ?? 'none');
  const [amount, setAmount] = useState(s.compensation?.amountCents ? String(s.compensation.amountCents / 100) : '');
  const [nameFr, setNameFr] = useState(s.nameFr ?? '');
  const [external, setExternal] = useState(Boolean(s.externalMembers));
  const [youth, setYouth] = useState(Boolean(s.youthDataApproved));
  const save = useMutation(
    async () => {
      const cents = kind === 'none' ? 0 : toCents(amount);
      if (Number.isNaN(cents)) throw { response: { data: { error: 'Amount looks like 100 or 100.00' } } };
      return api.patch(`/committees/${detail.program.key}/settings`, {
        compensation: { kind, amountCents: cents },
        nameFr,
        externalMembers: external,
        ...(role === 'super_admin' && youth !== Boolean(s.youthDataApproved) ? { youthDataApproved: youth } : {}),
      });
    },
    { onSuccess: onSaved },
  );
  return (
    <Modal
      title={`${detail.program.name}: settings`}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <Field title="Compensation per meeting attended">
          <select className={inputClass} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            {Object.entries(COMPENSATION_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Amount (CAD)">
          <input
            className={inputClass}
            disabled={kind === 'none'}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        <div className="col-span-2">
          <Field title="French name (for messages)">
            <input className={inputClass} value={nameFr} onChange={(e) => setNameFr(e.target.value)} />
          </Field>
        </div>
        <label className="col-span-2 flex items-center gap-2 text-table">
          <input type="checkbox" checked={external} onChange={() => setExternal(!external)} /> Members are mostly
          external contacts
        </label>
        {role === 'super_admin' ? (
          <label className="col-span-2 flex items-start gap-2 text-table">
            <input type="checkbox" className="mt-1" checked={youth} onChange={() => setYouth(!youth)} />
            <span>
              York’s privacy office has approved holding data about members under 18 (age band only; never a date of
              birth).
            </span>
          </label>
        ) : (
          <p className="col-span-2 text-small text-ink-2">
            Members under 18:{' '}
            {s.youthDataApproved
              ? 'held (approved by a super admin)'
              : 'not held until York’s privacy office has answered'}
            .
          </p>
        )}
      </div>
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save the settings.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------- page

export default function Committees() {
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const queryClient = useQueryClient();
  const [key, setKey] = useState<string | null>(null);
  const [cycleId, setCycleId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('roster');
  const [settings, setSettings] = useState(false);
  const committees = useQuery<Committee[]>('committees', async () => (await api.get('/committees')).data.data);
  useEffect(() => {
    if (!key && committees.data?.length) setKey(committees.data[0].key);
  }, [committees.data, key]);
  const committee = useMemo(() => committees.data?.find((c) => c.key === key) ?? null, [committees.data, key]);
  useEffect(() => {
    if (committee && !committee.cycles.some((c) => c.id === cycleId)) setCycleId(committee.cycles[0]?.id ?? null);
  }, [committee, cycleId]);
  const detail = useQuery<CycleDetail>(
    ['committee-cycle', cycleId],
    async () => (await api.get(`/committees/cycles/${cycleId}`)).data.data,
    { enabled: Boolean(cycleId) },
  );
  const newCycle = useMutation(
    async () =>
      api.post(`/committees/${key}/cycles`, { year: (committee?.cycles[0]?.year ?? new Date().getFullYear() - 1) + 1 }),
    {
      onSuccess: () => queryClient.invalidateQueries('committees'),
    },
  );
  const refresh = () => {
    queryClient.invalidateQueries('committees');
    queryClient.invalidateQueries('committee-cycle');
    queryClient.invalidateQueries('committee-selection');
    queryClient.invalidateQueries('committee-composition');
  };

  return (
    <>
      <PageHeader
        title="Committees"
        sub="Advisory committees and the Management Committee: who sits on each, recruitment and selection, meetings and attendance, and what is owed. Equity is shown only as aggregates; members under 18 are visible to admins only."
      />
      {committees.error ? <ErrorNotice>Could not load committees.</ErrorNotice> : null}
      <div className="flex flex-wrap gap-2 mb-4">
        {committees.data?.map((c) => (
          <Button
            key={c.id}
            size="sm"
            variant={c.key === key ? 'dark' : 'outline'}
            onClick={() => {
              setKey(c.key);
              setCycleId(null);
            }}
          >
            {c.name}
          </Button>
        ))}
      </div>
      {committee ? (
        <div className="flex flex-wrap items-stretch gap-3 mb-5">
          {committee.cycles.map((c) => {
            const total = Object.values(c.byStatus).reduce((a, b) => a + b, 0);
            return (
              <button
                key={c.id}
                onClick={() => setCycleId(c.id)}
                className={`text-left bg-white border px-4 py-3 min-w-[160px] ${c.id === cycleId ? 'border-line-strong border-2' : 'border-line'}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-[18px]">{c.label}</span>
                  <Pill tone={c.state === 'active' ? 'active' : c.state === 'closed' ? 'inactive' : 'planned'}>
                    {label(c.state)}
                  </Pill>
                </div>
                <div className="text-small text-ink-2 mt-1">{fmt(total)} on the roster</div>
              </button>
            );
          })}
          {mutable ? (
            <Button size="sm" disabled={newCycle.isLoading} onClick={() => newCycle.mutate()}>
              Next year’s cycle
            </Button>
          ) : null}
          {mutable && detail.data ? (
            <Button size="sm" variant="ghost" onClick={() => setSettings(true)}>
              Settings
            </Button>
          ) : null}
        </div>
      ) : null}
      {newCycle.error ? (
        <div className="mb-3">
          <ErrorNotice>{errorMessage(newCycle.error, 'Could not add the cycle.')}</ErrorNotice>
        </div>
      ) : null}
      {committee?.description ? <p className="text-small text-ink-2 mb-4">{committee.description}</p> : null}
      {detail.isLoading ? (
        <SkeletonRows rows={6} />
      ) : detail.error ? (
        <ErrorNotice>{errorMessage(detail.error, 'Could not load the cycle.')}</ErrorNotice>
      ) : detail.data ? (
        <>
          <div className="flex gap-2 mb-4 border-b border-line">
            {TABS.map(([t, l]) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-2 text-small ${tab === t ? 'border-b border-line-strong font-semibold' : 'text-ink-2'}`}
              >
                {l}
              </button>
            ))}
          </div>
          {tab === 'roster' ? (
            <Roster detail={detail.data} mutable={mutable} isAdmin={mutable} refresh={refresh} />
          ) : null}
          {tab === 'selection' ? <Selection detail={detail.data} mutable={mutable} refresh={refresh} /> : null}
          {tab === 'meetings' ? <Meetings detail={detail.data} mutable={mutable} refresh={refresh} /> : null}
          {tab === 'composition' ? <CompositionReport detail={detail.data} /> : null}
          {tab === 'polls' ? <Polls detail={detail.data} mutable={mutable} refresh={refresh} /> : null}
          {settings ? (
            <Settings
              detail={detail.data}
              role={user?.role}
              onClose={() => setSettings(false)}
              onSaved={() => {
                setSettings(false);
                refresh();
              }}
            />
          ) : null}
        </>
      ) : committee && !committee.cycles.length ? (
        <EmptyState title="No cycles yet">Add next year’s cycle.</EmptyState>
      ) : null}
    </>
  );
}
